import { keyframes, styled } from '@mui/material/styles';
import { Box, FormControlLabel, Typography } from '@mui/material';

export type FileUploadVariant =
  | 'default'
  | 'ai'
  | 'ai-verifier'
  | 'ai-ent-expense';

const isAiVariant = (v: FileUploadVariant) => v !== 'default';

const dotColorOf = (v: FileUploadVariant) =>
  isAiVariant(v) ? '#c77ac4' : '#5B9FE3';

const activeBorderOf = (v: FileUploadVariant) =>
  isAiVariant(v) ? '#dbaad9' : '#5c9dde';

// ===== Header (라벨 + 우측 체크박스) =====

export const FieldRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

export const FieldHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 8,
});

export const FieldCheckLabel = styled(FormControlLabel)({
  margin: 0,
  gap: 4,
  '& .MuiFormControlLabel-label': {
    fontSize: 14,
    lineHeight: '20px',
    color: 'var(--dk-text-primary, #34383D)',
  },
  '& .MuiCheckbox-root': {
    padding: 0,
    width: 18,
    height: 18,
  },
});

// ===== `.file-attachment` root =====
// 마우스 hover 또는 drag-active 시 동일 시각 (.active === :hover) — 자식 도트/보더/그라디언트 라인 토글.

export const FileAttachmentRoot = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'variantKey' && prop !== 'active' && prop !== 'disabled',
})<{
  variantKey: FileUploadVariant;
  active?: boolean;
  disabled?: boolean;
}>(({ variantKey, active, disabled }) => {
  const ai = isAiVariant(variantKey);
  const activeBorder = activeBorderOf(variantKey);

  // active 시각 — 자식 셀렉터로 토글
  const activeStyles = {
    '& .file-attachment-content': {
      borderColor: activeBorder,
    },
    '& .color-dots': {
      top: '50% !important',
      opacity: 1,
    },
    '& .color-dots-left, & .color-dots-right': {
      top: -90,
    },
    ...(ai && {
      '&::after': {
        background:
          'linear-gradient(to right, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.4) 20%, rgba(199,122,196,0.5) 50%, rgba(255,255,255,0.4) 80%, rgba(255,255,255,0.4) 100%)',
      },
    }),
  };

  return {
    position: 'relative',
    margin: 0,
    /** 좌·우 .color-dots-left/right 가 박스 외부로 새어나오지 않도록 root 자체에 overflow hidden.
     *  레거시 CSS는 .file-attachment-content 만 hidden 이지만, 좌/우 도트는 root 직속 자식이라
     *  root가 overflow:visible 이면 페이지 좌우로 도트가 흘러나감. */
    overflow: 'hidden',
    borderRadius: 4,
    userSelect: 'none' as const,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,

    // type-ai 박스 하단 1px 그라디언트 라인
    ...(ai && {
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: 1,
        background:
          'linear-gradient(to right, transparent 0%, transparent 40%, rgba(199,122,196,1) 50%, transparent 60%, transparent 100%)',
        zIndex: 2,
      },
    }),

    // active prop true 또는 마우스 hover — 동일 시각
    ...(active ? activeStyles : {}),
    ...(disabled ? {} : { '&:hover': activeStyles }),
  };
});

// ===== `.file-attachment-content` (보더 + 배경 + overflow hidden) =====

export const FileAttachmentContent = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid var(--dk-line, #e7e9ec)',
  borderRadius: 4,
  backgroundColor: 'var(--dk-bg-subtle, #fbfcfc)',
  transition: 'border-color 0.1s ease',
  // 박스 안 콘텐츠 패딩 — 사용자 레퍼런스 스크린샷 기준 위·아래 28~32, 좌·우 24
  padding: '28px 24px',
});

// ===== `.color-dots` (메인 도트, 박스 하단/가운데) =====
// idle: top 95% + opacity 0.7 — 박스 아래쪽에 옅게
// active: top 50% + opacity 1 — 박스 가운데로 슬라이드 + 진해짐

export const ColorDots = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variantKey',
})<{ variantKey: FileUploadVariant }>(({ variantKey }) => {
  const dot = dotColorOf(variantKey);
  const mask =
    'radial-gradient(ellipse 240px 200px at 50% 50%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 30%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.1) 85%, transparent 100%), linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 100%)';
  return {
    position: 'absolute',
    top: '95%',
    right: 0,
    bottom: 0,
    left: 0,
    backgroundImage: `radial-gradient(circle, ${dot} 1px, transparent 1px)`,
    backgroundSize: '8px 8px',
    backgroundPosition: '40% 0',
    WebkitMaskImage: mask,
    maskImage: mask,
    maskComposite: 'intersect' as const,
    WebkitMaskComposite: 'source-in' as const,
    pointerEvents: 'none' as const,
    transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
    opacity: 0.7,
  };
});

// ===== `.color-dots-left` / `.color-dots-right` (좌상/우상 추가 도트) =====
// idle: top -150px (거의 안 보임). active: top -90px (박스 위쪽으로 살짝 들어옴).
// `.file-attachment-content` 의 overflow:hidden 을 피해야 하므로 root 직속 자식.
// 모바일(<=767px)에서는 숨김.

const sideDotsBase = (variantKey: FileUploadVariant) => {
  const dot = dotColorOf(variantKey);
  const mask =
    'radial-gradient(ellipse 300px 200px at 50% 50%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 30%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.1) 85%, transparent 100%), linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 100%)';
  return {
    position: 'absolute' as const,
    top: -150,
    width: 300,
    height: 200,
    pointerEvents: 'none' as const,
    backgroundImage: `radial-gradient(circle, ${dot} 1px, transparent 1px)`,
    backgroundSize: '8px 8px',
    backgroundPosition: '40% 0',
    WebkitMaskImage: mask,
    maskImage: mask,
    maskComposite: 'intersect' as const,
    WebkitMaskComposite: 'source-in' as const,
    transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '@media screen and (max-width: 767px)': {
      display: 'none',
    },
  };
};

export const ColorDotsLeft = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variantKey',
})<{ variantKey: FileUploadVariant }>(({ variantKey }) => ({
  ...sideDotsBase(variantKey),
  left: '-2%',
}));

export const ColorDotsRight = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variantKey',
})<{ variantKey: FileUploadVariant }>(({ variantKey }) => ({
  ...sideDotsBase(variantKey),
  right: '-2%',
}));

// ===== Inner (텍스트 + 버튼) =====

export const Inner = styled(Box)({
  position: 'relative',
  zIndex: 1,
  textAlign: 'center',
});

export const HelpTextPrimary = styled(Typography)({
  textAlign: 'center',
  marginTop: 0,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  color: 'var(--dk-text, #1A1A1A)',
});

export const HelpTextSecondary = styled(Typography)({
  marginTop: 4,
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16px',
  color: 'var(--dk-text-secondary, #888D96)',
});

export const UploadButtonWrap = styled(Box)({
  marginTop: 14,
  display: 'flex',
  justifyContent: 'center',
});

// ===== Upload button (.btn-file-upload) =====
// 부모 hover (root :hover) 에서 보더 색 변경은 root 측 셀렉터로 처리하지 않고
// 여기 자체 :hover (box-shadow) 만 처리. 부모 hover 보더 변경은 root 측에서 자식 셀렉터로 처리.

export const UploadButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'variantKey',
})<{ variantKey: FileUploadVariant }>(({ variantKey }) => {
  const ai = isAiVariant(variantKey);
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    height: 36,
    padding: '0 8px',
    borderRadius: 8,
    border: ai
      ? '1px solid var(--dk-purple-line, #F2DFF1)'
      : '1px solid var(--dk-line, #E7E9EC)',
    background: 'var(--dk-bg, #FFF)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    color: 'var(--dk-text-primary, #34383D)',
    boxShadow: ai ? '0 0 16px 0 rgba(199,122,196,0.16)' : 'none',
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',

    '& > img': {
      width: 16,
      height: 16,
      flexShrink: 0,
      display: 'block',
    },

    '& > .upload-label': {
      padding: '0 4px',
    },

    '&:hover': {
      // 부모 .file-attachment 가 cursor:pointer 지만, 버튼 위에서도 동일한 손가락 커서로 명시
      cursor: 'pointer',
      boxShadow: ai
        ? '0 0 16px 0 rgba(199,122,196,0.24)'
        : '0 0 16px 0 rgba(122, 163, 199, 0.16)',
    },

    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
      boxShadow: 'none',
    },

    // type-ai 버튼 상단 1px 그라디언트 라인
    ...(ai && {
      '&::after': {
        content: '""',
        position: 'absolute',
        top: -1,
        left: 0,
        width: '100%',
        height: 1,
        background:
          'linear-gradient(to right, transparent 0%, transparent 40%, rgba(199,122,196,0.6) 50%, transparent 60%, transparent 100%)',
        zIndex: 2,
      },
      '&:hover::after': {
        background:
          'linear-gradient(to right, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.4) 20%, rgba(199,122,196,0.5) 50%, rgba(255,255,255,0.4) 80%, rgba(255,255,255,0.4) 100%)',
      },
    }),
  };
});

// ===== Hidden file input =====

export const HiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  clip: 'rect(0 0 0 0)',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

// ===== Progress bar =====

export const ProgressTrack = styled(Box)({
  position: 'relative',
  width: '100%',
  height: 4,
  marginTop: 16,
  borderRadius: 2,
  backgroundColor: 'var(--dk-bg-muted, #F3F5F6)',
  overflow: 'hidden',
});

export const ProgressFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variantKey' && prop !== 'progress',
})<{ variantKey: FileUploadVariant; progress: number }>(
  ({ variantKey, progress }) => {
    const ai = isAiVariant(variantKey);
    return {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: `${Math.max(0, Math.min(100, progress))}%`,
      backgroundColor: ai ? '#c77ac4' : '#5B9FE3',
      transition: 'width 0.2s ease',
    };
  },
);

// ===== AI 변이 전용 (.ai-tag / .loading / 하단 안내) =====

export const AiTag = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  marginBottom: 12,
  padding: '4px 12px',
  borderRadius: 20,
  backgroundColor: 'rgba(199,122,196,0.12)',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '18px',
  color: '#c77ac4',
});

// root 의 overflow·radius 안에서 박스 전체를 덮음. 도트(zIndex 없음)·하단 라인(2) 위
export const AiLoadingOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  padding: '0 16px',
  backgroundColor: 'rgba(255,252,255,0.8)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  cursor: 'default',
  textAlign: 'center',
});

export const AiLoadingText = styled('p')({
  margin: 0,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '21px',
  color: '#c77ac4',
  '& > span + span': {
    marginLeft: 4,
  },
  '& > .block': {
    display: 'block',
    marginLeft: 0,
  },
});

export const AiCancelButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: 10,
  borderRadius: 8,
  border: '1px solid var(--dk-purple-line, #F2DFF1)',
  backgroundColor: 'var(--dk-bg, #FFF)',
  boxShadow: '0 0 16px 0 rgba(199,122,196,0.16)',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
  color: '#FF0000',
  cursor: 'pointer',
});

const aiSpin = keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' },
});

export const AiSpinner = styled('span')({
  display: 'block',
  width: 20,
  height: 20,
  boxSizing: 'border-box',
  borderRadius: '50%',
  border: '2px solid #DDDDDD',
  borderTopColor: '#c77ac4',
  animation: `${aiSpin} 1s linear infinite`,
});

export const AiNotice = styled('p')({
  margin: '10px 0 0',
  textAlign: 'center',
  fontSize: 14,
  lineHeight: '22px',
  color: 'var(--dk-text-secondary, #888D96)',
});
